import HelpIcon from '@mui/icons-material/Help';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { type NewCustomer, type UpdateCustomer } from '@vy/types';
import { isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CurrenciesSelect } from '../../../../../../components/design/selects/CurrenciesSelect';
import { InvoiceDueDateSelect } from '../../../../../../components/design/selects/InvoiceDueDateSelect';
import { VySelect } from '../../../../../../components/new-design/atoms';
import { useAnrokDefaultTaxMethod } from '../../../../../../hooks/requests/use-anrok-default-tax-method';
import { useIsTaxIntegrationsConnected } from '../../../../../../hooks/requests/useIsTaxIntegrationsConnected';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import type { CreateCustomerMode } from '../../../create-customer-page-types/CreateCustomerModeType';
import { ShareCustomerInvoices } from './ShareCustomerInvoices';
import { VyFormSection } from '../../../../../../components/design/layout';
import { VyIcon } from '@vy/ui-design-system';
import { colors } from '../../../../../../assets/themes/colors.ts';
import { ANROK_DEFAULT_TAX_METHOD } from '@vy/integrations-types';
import { TaxRateMode } from '../../../../customers-component/tax-rate-mode';

type CustomerInvoicesConfigurationProps = CreateCustomerMode & {
  customerId?: string;
};

export const CustomerInvoicesConfiguration: React.FC<CustomerInvoicesConfigurationProps> = ({
  mode = 'create',
  customerId,
}) => {
  const isTaxIntegrationsConnected = useIsTaxIntegrationsConnected();
  const anrokDefaultTaxMethod = useAnrokDefaultTaxMethod();
  const [taxRateMode, setTaxRateMode] = useState<TaxRateMode>(TaxRateMode.AUTO);

  const { getValues, register, formState, setValue } =
    useFormContext<typeof mode extends 'create' ? NewCustomer : UpdateCustomer>();

  const taxRate = getValues('settings.taxRate');
  const initialTaxRateRef = useRef<number | null>(taxRate ?? 0);
  const hasUserSelectedModeRef = useRef(false);

  useEffect(() => {
    if (hasUserSelectedModeRef.current || mode === 'create') return;

    if (!isTaxIntegrationsConnected) {
      setTaxRateMode(TaxRateMode.MANUAL);

      if (isNullOrUndefined(taxRate)) {
        setValue('settings.taxRate', 0, { shouldDirty: true });
      }

      return;
    }
  }, [mode, taxRate, isTaxIntegrationsConnected]);

  useEffect(() => {
    if (hasUserSelectedModeRef.current) return;

    if (!isTaxIntegrationsConnected) {
      setTaxRateMode(TaxRateMode.MANUAL);

      if (isNullOrUndefined(taxRate)) {
        setValue('settings.taxRate', 0, { shouldDirty: true });
      }

      return;
    }

    switch (mode) {
      case 'create': {
        if (isNullOrUndefined(taxRate)) {
          const defaultMode =
            anrokDefaultTaxMethod === ANROK_DEFAULT_TAX_METHOD.MANUAL ? TaxRateMode.MANUAL : TaxRateMode.AUTO;

          setTaxRateMode(defaultMode);

          if (defaultMode === TaxRateMode.MANUAL) {
            setValue('settings.taxRate', initialTaxRateRef.current, { shouldDirty: true });
          }
        } else {
          setTaxRateMode(TaxRateMode.MANUAL);
        }
        break;
      }
      case 'update': {
        setTaxRateMode(isNullOrUndefined(taxRate) ? TaxRateMode.AUTO : TaxRateMode.MANUAL);
        break;
      }
    }
  }, [mode, isTaxIntegrationsConnected, anrokDefaultTaxMethod]);

  const handleTaxRateModeSelect = (selected: TaxRateMode) => {
    hasUserSelectedModeRef.current = true;

    if (selected === TaxRateMode.AUTO) {
      setValue('settings.taxRate', null, { shouldDirty: true });
    }

    if (selected === TaxRateMode.MANUAL && isNullOrUndefined(taxRate)) {
      setValue('settings.taxRate', initialTaxRateRef.current, { shouldDirty: true });
    }

    setTaxRateMode(selected);
  };

  return (
    <VyFormSection
      title="Invoices"
      icon={<VyIcon icon="InvoiceList" height={18} width={18} viewBox="0 0 24 24" color={colors.grey[90]} />}
      actions={customerId && <ShareCustomerInvoices customerId={customerId} />}
    >
      <Grid container rowSpacing={3} columnSpacing={2} size={12}>
        <Grid size={3}>
          <Controller
            name="settings.currency"
            render={({ field }) => (
              <CurrenciesSelect
                error={isNotNullOrUndefined(formState.errors.settings?.currency)}
                helperText={formState.errors.settings?.currency?.message}
                {...field}
                value={field.value ?? ''}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid size={taxRateMode === TaxRateMode.MANUAL ? 3 : 4}>
          <VySelect
            label={
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1
                }}>
                <Typography sx={{
                  fontSize: 12
                }}>Tax rate method</Typography>
                <Tooltip
                  title="Choose how the tax rate is set: calculated automatically or manually defined by you"
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'rgba(13, 27, 39, 0.87)',
                        borderRadius: '4px',
                        fontSize: '10px',
                        maxWidth: '167px',
                      },
                    },
                    arrow: {
                      sx: {
                        color: 'rgba(13, 27, 39, 0.87)',
                      },
                    },
                  }}
                  arrow
                >
                  <HelpIcon sx={{ fontSize: 16, color: 'rgba(13, 27, 39, 0.30)' }} />
                </Tooltip>
              </Stack>
            }
            options={Object.values(TaxRateMode).map((mode) => ({ key: mode }))}
            onSelectionChange={(item) => handleTaxRateModeSelect(item?.key as TaxRateMode)}
            selectedOption={{ key: taxRateMode }}
            selectProps={{
              sx: {
                background: '#F5F6FA',
              },
            }}
            shouldDisableOption={(option) => !isTaxIntegrationsConnected && option.key === TaxRateMode.AUTO}
            keyExtractor={(o) => String(o.key)}
            fullWidth
            render={(o) => (
              <Typography>
                {o.key}
                {!isTaxIntegrationsConnected && o.key === TaxRateMode.AUTO && (
                  <Typography>(requires tax provider)</Typography>
                )}
              </Typography>
            )}
          />
        </Grid>

        {taxRateMode === TaxRateMode.MANUAL && (
          <Grid size={2}>
            <TextField
              {...getSelectorAttribute(createCustomerPageSelectors.configuration.tax)}
              label="Tax"
              defaultValue={getValues('settings.taxRate') ?? null}
              type="number"
              fullWidth
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: {
                  step: 'any',
                },
              }}
              onWheel={(e) => {
                const target = e.target as HTMLInputElement;

                target.blur();
              }}
              error={isNotNullOrUndefined(formState.errors.settings?.taxRate)}
              helperText={formState.errors.settings?.taxRate?.message}
              {...vyFormRegister(register, 'settings.taxRate', {}, (value) => {
                if (isNullOrUndefined(value) || isEmptyString(value)) {
                  return null;
                }

                return Number(String(value)?.trim());
              })}
              onBlur={(e) => {
                const value = e.target.value;

                if (isEmptyString(value)) {
                  setValue('settings.taxRate', 0, { shouldDirty: true });
                }
              }}
            />
          </Grid>
        )}
        <Grid size={taxRateMode === TaxRateMode.MANUAL ? 2 : 3}>
          <Controller
            name="settings.paymentDueDays"
            render={({ field }) => (
              <InvoiceDueDateSelect
                error={isNotNullOrUndefined(formState.errors.settings?.paymentDueDays)}
                helperText={formState.errors.settings?.paymentDueDays?.message}
                {...field}
                defaultValue={getValues('settings.paymentDueDays')}
                value={field.value ?? ''}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid size={2}>
          <FormControlLabel
            control={
              <Checkbox
                {...getSelectorAttribute(createCustomerPageSelectors.configuration.manualReview)}
                {...vyFormRegister(register, 'settings.manualReview')}
                defaultChecked={getValues('settings.manualReview')}
              />
            }
            sx={{
              alignContent: 'center',
              height: '100%',
              paddingTop: '30px',
            }}
            label="Manual Review"
          />
        </Grid>
      </Grid>
    </VyFormSection>
  );
};
